import { isTextEditableElement, MenuV2 } from '@figma/fpl-components';
import { useCallback, useMemo, useRef, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { MenuV2Items } from './MenuV2Items';
import { computeMenuPosition, resolveAnchor } from './menuV2Utils';
import type { AppContextMenuProps } from './AppContextMenu.types';

function isUnmodifiedPrintableKey(event: KeyboardEvent): boolean {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}

const DEFAULT_MENU_COLOR_THEME = { brand: 'weave', mode: 'dark' } as const;

export function FplAppContextMenu({
  open,
  onClose,
  anchorEl,
  mouseX,
  mouseY,
  anchorOrigin,
  transformOrigin,
  offset,
  header,
  items,
  children,
  colorTheme = DEFAULT_MENU_COLOR_THEME,
  onKeyDownCapture,
}: AppContextMenuProps) {
  const { element, point } = useMemo(() => resolveAnchor(anchorEl, mouseX, mouseY), [anchorEl, mouseX, mouseY]);

  const anchorRef = useRef<HTMLElement | null>(null);
  // Set during render (not an effect) so the anchor is available before MenuV2's Popup
  // runs its layout-effect positioning on the same commit.
  if (element) anchorRef.current = element;

  const isOpen = Boolean(open) && (element != null || point != null);

  const requestClose = useCallback(() => onClose(), [onClose]);

  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDownCapture?.(event);
      if (event.isPropagationStopped()) return;
      if (!isTextEditableElement(event.target) || !isUnmodifiedPrintableKey(event)) return;

      // MenuV2 owns printable keys for typeahead in capture phase. This boundary is above
      // MenuV2.Root in the React tree, so it also runs before MenuV2 for portaled submenus.
      // Stop propagation without preventing the browser's default text-editing action.
      event.stopPropagation();
    },
    [onKeyDownCapture],
  );

  const { manager } = MenuV2.useMenu({
    open: isOpen,
    anchorRef,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) requestClose();
    },
  });

  const position = useMemo(
    () => computeMenuPosition(anchorOrigin, transformOrigin, offset),
    [anchorOrigin, transformOrigin, offset],
  );

  const usePointAnchor = element == null && point != null;
  return (
    <>
      {isOpen &&
        usePointAnchor &&
        point &&
        // Portal the anchor to <body>: `position: fixed` is relative to the nearest transformed
        // ancestor, and point-anchored menus (prompt-node mention/tag menus, canvas float menu) live
        // inside the pan/zoom-transformed React Flow canvas. Rendering the anchor inline there offsets
        // it by the canvas transform, so the menu lands far from the trigger. The MenuV2 popup already
        // portals to <body>, so anchoring off a body-level fixed point keeps them in the same frame.
        createPortal(
          <span
            aria-hidden
            ref={(el) => {
              if (el) anchorRef.current = el;
            }}
            style={{
              position: 'fixed',
              left: point.x,
              top: point.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
              opacity: 0,
            }}
          />,
          document.body,
        )}
      <div style={{ display: 'contents' }} onKeyDownCapture={handleKeyDownCapture}>
        <MenuV2.Root manager={manager} position={position} colorTheme={colorTheme}>
          {header ? <li role="presentation">{header}</li> : null}
          {items ? <MenuV2Items items={items} requestClose={requestClose} /> : children}
        </MenuV2.Root>
      </div>
    </>
  );
}
